import {
	Box,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	Icon,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import CloseIcon from "@mui/icons-material/Close";
import { ptBR } from "date-fns/locale";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { Environment } from "../../../shared/environment";
import { useToast } from "../../../shared/hooks";
import { IProvisionListBySinister, ProvisionService } from "../../../shared/services/api/provisoes/ProvisionService";
import { globalBackground, globalColorText, GlobalCoolorIconSinister, globalTranslate } from "../../../shared/utils";
import { TableDetailBordero } from "./TableDetailBordero";

const normalizeStatusForSoftChip = (status: string) => {
	if (status === "approved" || status === "accepted") return "issued";
	if (status === "provision") return "provisioned";
	if (status === "analysis") return "pending";
	return status;
};

interface Props {
	SinisterId: string;
}

export function TableBordero({ SinisterId }: Props) {
	const [rows, setRowsProvision] = useState<IProvisionListBySinister[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [openModalDetail, setOpenModalDetail] = useState(false);
	const [selectedBorderoId, setSelectedBorderoId] = useState<string | null>(null);

	const toastHandler = useToast();

	const handleFetchBorderos = async () => {
		try {
			setIsLoading(true);
			const result = await ProvisionService.getAllBorderoById(SinisterId);

			if (result instanceof Error) {
				toastHandler.present({
					type: "error",
					messageOrContent: (
						<ToastContent title="Erro ao carregar borderôs" content={result.message || "Não foi possível buscar os dados do borderô."} />
					),
				});
				return;
			}

			setRowsProvision(result.data);
			setTotalCount(result.totalCount);
		} catch (error: any) {
			toastHandler.present({
				type: "error",
				messageOrContent: <ToastContent title="Erro inesperado" content={error?.message || "Algo deu errado ao carregar os dados."} />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		handleFetchBorderos();
	}, []);

	useEffect(() => {
		setPage(0);
	}, [SinisterId]);

	const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	const handleOpenDetailModal = (id: string) => {
		setSelectedBorderoId(id);
		setOpenModalDetail(true);
	};

	const handleCloseDetailModal = () => {
		setOpenModalDetail(false);
		setSelectedBorderoId(null);
	};

	return (
		<>
			<LoadingComp isopen={isLoading} />
			<Grid container item direction="row" spacing={2}>
				<Grid item xs={12}>
					<TableContainer component={Paper} elevation={0} variant="elevation" sx={{ borderRadius: 0, overflow: "hidden", boxShadow: "none" }}>
						<Table size="small" stickyHeader aria-label="Tabela de borderôs">
							<TableHead>
								<TableRow sx={{ backgroundColor: "grey.100" }}>
									<TableCell sx={{ width: "1%" }}>
										<Typography variant="button">#</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="button">Criação</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="button">Limite</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="button">Provisão</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="button">Valor</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="button">Status</Typography>
									</TableCell>
									<TableCell align="right" sx={{ p: 0, width: "1%" }}>
										<Typography variant="button">Ações</Typography>
									</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{pagedRows.map((row) => (
									<TableRow
										key={row.id}
										hover
										sx={{
											"& .MuiTableCell-root": {
												py: 0.75,
											},
										}}
									>
										<TableCell>
											<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
												{row.sequencial}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Stack spacing={0.25}>
												<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
													{row.createdAt ? format(new Date(row.createdAt), "P", { locale: ptBR }) : "-"}
												</Typography>
												<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
													{row.createdAt ? format(new Date(row.createdAt), "HH:mm:ss") : ""}
												</Typography>
											</Stack>
										</TableCell>
										<TableCell align="center">
											<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
												{row.dueDate ? format(new Date(row.dueDate), "P", { locale: ptBR }) : "-"}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
												{row.provisionedAt ? format(new Date(row.provisionedAt), "P", { locale: ptBR }) : "-"}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ lineHeight: 1.2 }}>
												{row.value != null
													? parseFloat(`${row.value}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" })
													: "-"}
											</Typography>
										</TableCell>
										<TableCell>
											<Box display="flex" flexDirection="column" gap={0.5}>
												<Chip
													size="medium"
													label={globalTranslate(row.status)}
													sx={{
														color: globalColorText(normalizeStatusForSoftChip(row.status)),
														backgroundColor: globalBackground(normalizeStatusForSoftChip(row.status)),
														fontWeight: 500,
														border: "0.5px solid #f1f1f1",
														width: "fit-content",
													}}
												/>
												{row.paymentAt && (
													<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
														{format(new Date(row.paymentAt), "P", { locale: ptBR })}
													</Typography>
												)}
											</Box>
										</TableCell>
										<TableCell align="right" sx={{ p: 0, width: "1%" }}>
											<IconButton size="small" onClick={() => handleOpenDetailModal(row.id)}>
												<Icon sx={{ color: GlobalCoolorIconSinister(row.status) }}>remove_red_eye_Outlined_Icon</Icon>
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>

						{totalCount === 0 && !isLoading && <caption>{Environment.LISTAGEM_VAZIA}</caption>}

						{/* Paginação (padrão financeiro) */}
						{totalCount > 0 && (
							<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
								<TablePagination
									component="div"
									count={totalCount}
									page={page}
									rowsPerPage={rowsPerPage}
									onPageChange={(_, newPage) => setPage(newPage)}
									onRowsPerPageChange={(e) => {
										setRowsPerPage(parseInt(e.target.value, 10));
										setPage(0);
									}}
									rowsPerPageOptions={[10, 25, 50]}
									labelRowsPerPage="Linhas por página:"
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
								/>
							</Box>
						)}
					</TableContainer>
				</Grid>
			</Grid>

			{/* Modal com detalhe do borderô */}
			<Dialog open={openModalDetail} onClose={handleCloseDetailModal} fullWidth maxWidth="md">
				<DialogTitle>
					<Grid container justifyContent="space-between" alignItems="center">
						<Typography variant="h5" sx={{ color: "primary.main", fontWeight: 300 }}>
							Detalhes do Borderô {rows.find((row) => row.id === selectedBorderoId)?.sequencial}
						</Typography>
						<IconButton onClick={handleCloseDetailModal} size="small">
							<CloseIcon />
						</IconButton>
					</Grid>
				</DialogTitle>
				<DialogContent dividers>{selectedBorderoId && <TableDetailBordero BorderoId={selectedBorderoId} disableFetch={false} />}</DialogContent>
			</Dialog>
		</>
	);
}
